import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { useAuth } from './AuthContext';
import { UserSite } from '../types/SiteAdmin';
import { HistoryEvent } from '../utils/eventHelpers';

type Roles = Array<'global_admin' | 'participant'>;

interface UnifiedUser {
  userId?: string;
  username?: string;
  phoneNumber?: string;
  roles: Roles;
  // Avatar customization
  avatarType?: 'initial' | 'image';
  avatarInitial?: string;
  avatarColor?: string;
  avatarUrl?: string;
  // participant-specific
  participant?: {
    memberId: string;
    name: string;
    phoneNumber: string;
    verified: boolean;
  } | null;
}

export interface AnnouncementWithEvent {
  _id: string;
  title: string;
  message: string;
  isImportant: boolean;
  createdAt: string;
  createdBy: string;
  visibility?: 'public' | 'private';
  event: {
    _id: string;
    title: string;
    date: string;
    time: string;
    location: string;
    siteName: string;
    siteRoute: string;
  };
}

interface OtpLoginResult {
  token: string;
  type: 'admin' | 'participant';
  route?: string;
}

export interface JoinedSite {
  siteId: string;
  siteName: string;
  siteRoute: string;
  joinedAt: string;
}

interface UserContextValue {
  loading: boolean;
  user: UnifiedUser | null;
  userSites: UserSite[];
  joinedSites: JoinedSite[];
  participantHistory: HistoryEvent[];
  announcements: AnnouncementWithEvent[];
  unreadAnnouncementsCount: number;
  displayName: string;
  displayInitial: string;
  isAdmin: boolean;
  isParticipant: boolean;
  refresh: () => Promise<void>;
  refreshUserSites: () => Promise<void>;
  refreshParticipantHistory: () => Promise<void>;
  refreshAnnouncements: () => Promise<void>;
  refreshAll: () => Promise<void>; // Refresh all data
  markAnnouncementsAsRead: () => void; // Mark all announcements as read
  loginWithPassword: (username: string, password: string) => Promise<void>;
  sendOtp: (phoneNumber: string) => Promise<void>;
  loginWithOtp: (phoneNumber: string, code: string) => Promise<OtpLoginResult | undefined>;
  logout: () => void;
}

const UserContext = createContext<UserContextValue | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<UnifiedUser | null>(null);
  const [userSites, setUserSites] = useState<UserSite[]>([]);
  const [joinedSites, setJoinedSites] = useState<JoinedSite[]>([]);
  const [participantHistory, setParticipantHistory] = useState<HistoryEvent[]>([]);
  const [announcements, setAnnouncements] = useState<AnnouncementWithEvent[]>([]);
  const [unreadAnnouncementsCount, setUnreadAnnouncementsCount] = useState(0);
  const { logout: authLogout } = useAuth();

  const refreshUserSites = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API_BASE_URL}/site-admin/my-sites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserSites(response.data.sites || []);
    } catch (error) {
      setUserSites([]);
    }
  }, []);

  const refreshParticipantHistory = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API_BASE_URL}/auth/participant-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setParticipantHistory(response.data?.history || []);
      setJoinedSites(response.data?.sites || []);
    } catch (error) {
      setParticipantHistory([]);
      setJoinedSites([]);
    }
  }, []);

  const refreshAnnouncements = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API_BASE_URL}/auth/participant-announcements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const fetchedAnnouncements = response.data.announcements || [];
      setAnnouncements(fetchedAnnouncements);
      
      // Get last read timestamp from localStorage
      const lastReadTimestamp = localStorage.getItem('announcements_last_read');
      
      // Count unread announcements (created after last read time)
      if (lastReadTimestamp) {
        const unreadCount = fetchedAnnouncements.filter(
          (announcement: AnnouncementWithEvent) => 
            new Date(announcement.createdAt).getTime() > parseInt(lastReadTimestamp)
        ).length;
        setUnreadAnnouncementsCount(unreadCount);
      } else {
        // If never read before, all are unread
        setUnreadAnnouncementsCount(fetchedAnnouncements.length);
      }
    } catch (error) {
      setAnnouncements([]);
      setUnreadAnnouncementsCount(0);
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setUser(null);
        setUserSites([]);
        setParticipantHistory([]);
        setAnnouncements([]);
        setUnreadAnnouncementsCount(0);
        return;
      }
      const res = await axios.get(`${API_BASE_URL}/auth/check-token`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data || {};
      const roles = (data.roles as Roles) || ([] as Roles);
      const isParticipant = roles.includes('participant');
      
      setUser({
        userId: data.userId,
        username: data.username,
        phoneNumber: data.phoneNumber,
        roles: roles.includes('participant') ? roles : [...roles, 'participant'],
        // Avatar customization
        avatarType: data.avatarType,
        avatarInitial: data.avatarInitial,
        avatarColor: data.avatarColor,
        avatarUrl: data.avatarUrl,
        participant: {
          memberId: data.userId,
          name: data.name,
          phoneNumber: data.phoneNumber,
          verified: data.verified,
        }
      });
      
      // Fetch user-specific data
      const promises = [];
      // Always fetch user sites to check if user is site admin
      promises.push(refreshUserSites());
      if (isParticipant) {
        promises.push(refreshParticipantHistory());
        promises.push(refreshAnnouncements());
      }
      await Promise.all(promises);
    } catch (e) {
      // Silently reset
      setUser(null);
      setUserSites([]);
      setParticipantHistory([]);
      setAnnouncements([]);
      setUnreadAnnouncementsCount(0);
    } finally {
      setLoading(false);
    }
  }, [refreshUserSites, refreshParticipantHistory, refreshAnnouncements]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const loginWithPassword = useCallback(async (username: string, password: string) => {
    const res = await axios.post(`${API_BASE_URL}/auth/login`, { username, password });
    const { token } = res.data;
    localStorage.setItem('token', token);
    await refresh();
  }, [refresh]);

  const sendOtp = useCallback(async (phoneNumber: string) => {
    // Use the new send-otp endpoint for phone login
    await axios.post(`${API_BASE_URL}/auth/send-otp`, { phoneNumber });
  }, []);

  const loginWithOtp = useCallback(async (phoneNumber: string, code: string) => {
    const res = await axios.post(`${API_BASE_URL}/auth/phone-login`, { phoneNumber, code });
    const { token, roles, route } = res.data || {};
    localStorage.setItem('token', token);
    await refresh();
    return { token, type: roles?.includes('global_admin') ? 'admin' : 'participant', route } as any;
  }, [refresh]);

  const refreshAll = useCallback(async () => {
    const promises = [];
    // Always fetch user sites to check admin status
    promises.push(refreshUserSites());
    if (user?.roles?.includes('participant')) {
      promises.push(refreshParticipantHistory());
      promises.push(refreshAnnouncements());
    }
    await Promise.all(promises);
  }, [user, refreshUserSites, refreshParticipantHistory, refreshAnnouncements]);

  const markAnnouncementsAsRead = useCallback(() => {
    // Store current timestamp as last read time
    const now = Date.now().toString();
    localStorage.setItem('announcements_last_read', now);
    // Reset unread count to 0
    setUnreadAnnouncementsCount(0);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setUserSites([]);
    setJoinedSites([]);
    setParticipantHistory([]);
    setAnnouncements([]);
    setUnreadAnnouncementsCount(0);
    authLogout(); // Also logout from AuthContext
  }, [authLogout]);

  const value: UserContextValue = useMemo(() => {
    // isAdmin: user is global_admin OR has any sites they manage
    const isAdmin = !!user?.roles?.includes('global_admin') || (userSites && userSites.length > 0);
    const isParticipant = !!user?.roles?.includes('participant');
    // Prefer participant name (display name) over username for better UX
    // Fallback order: participant.name → username → phoneNumber → 'User'
    const displayName = user?.participant?.name || user?.username || user?.phoneNumber || 'User';
    const displayInitial = displayName.charAt(0).toUpperCase();
    
    return {
      loading,
      user,
      userSites,
      joinedSites,
      participantHistory,
      announcements,
      unreadAnnouncementsCount,
      displayName,
      displayInitial,
      isAdmin,
      isParticipant,
      refresh,
      refreshUserSites,
      refreshParticipantHistory,
      refreshAnnouncements,
      refreshAll,
      markAnnouncementsAsRead,
      loginWithPassword,
      sendOtp,
      loginWithOtp,
      logout,
    };
  }, [
    loading, 
    user, 
    userSites,
    joinedSites,
    participantHistory, 
    announcements, 
    unreadAnnouncementsCount, 
    refresh, 
    refreshUserSites, 
    refreshParticipantHistory, 
    refreshAnnouncements, 
    refreshAll,
    markAnnouncementsAsRead,
    loginWithPassword, 
    sendOtp, 
    loginWithOtp, 
    logout
  ]);

  return (
    <UserContext.Provider value={value}>{children}</UserContext.Provider>
  );
};

export const useUser = () => {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error('useUser must be used within a UserProvider');
  return ctx;
};


